import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Switch,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Link as RouterLink } from "react-router-dom";
import { CustomButton } from "@/components/common/customButton";
import { useAuthContext } from "@/context/AuthContext";
import { useCurrentUser } from "@/context/CurrentUserContext";
import { useSocket } from "@/context/SocketContext";
import { useTenantFeatures } from "@/context/TenantFeaturesContext";
import "@/assets/css/Settings.css";

type SettingsSection = "account" | "notifications" | "workspace";

function SettingsCard({
  id,
  title,
  icon,
  children,
}: {
  id: SettingsSection;
  title: string;
  icon: React.ReactNode;
  children: ReactNode;
}) {
  return (
    <section id={id} className="settings-card">
      <div className="settings-card-header">
        <div className="settings-card-icon">{icon}</div>
        <h2 className="settings-card-title">{title}</h2>
      </div>
      <div className="settings-card-body">{children}</div>
    </section>
  );
}

export default function Settings() {
  const { userSummary } = useAuthContext();
  const { canEdit } = useCurrentUser();
  const { config } = useTenantFeatures();
  const {
    showNotifications,
    notificationSettingsLoading,
    notificationSettingsLoadError,
    savingNotificationSettings,
    saveNotificationSettingsError,
    saveNotificationSettings,
  } = useSocket();

  const [draftShowNotifications, setDraftShowNotifications] =
    useState(showNotifications);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    setDraftShowNotifications(showNotifications);
  }, [showNotifications]);

  const isDirty = useMemo(
    () => draftShowNotifications !== showNotifications,
    [draftShowNotifications, showNotifications],
  );

  const handleSave = useCallback(async () => {
    setSaveSuccess(false);
    const didSave = await saveNotificationSettings(draftShowNotifications);
    if (didSave) {
      setSaveSuccess(true);
    }
  }, [draftShowNotifications, saveNotificationSettings]);

  const handleToggle = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    setSaveSuccess(false);
    setDraftShowNotifications(checked);
  };

  return (
    <Box className="settings-page">
      <Box className="settings-content">
        <SettingsCard
          id="account"
          title="Account"
          icon={<PersonOutlineIcon />}
        >
          <span className="settings-field-label">Name</span>
          <p className="settings-field-value">
            {userSummary?.displayName || "—"}
          </p>

          <span className="settings-field-label">Email</span>
          <p className="settings-field-value">
            {userSummary?.email || "—"}
          </p>

          {config?.display_name ? (
            <>
              <span className="settings-field-label">Workspace</span>
              <div className="settings-workspace-row">
                <p className="settings-field-value" style={{ margin: 0 }}>
                  {config.display_name}
                </p>
                <span className="settings-active-badge">• Active</span>
              </div>
            </>
          ) : null}
        </SettingsCard>

        <SettingsCard
          id="notifications"
          title="Notifications"
          icon={<NotificationsNoneOutlinedIcon />}
        >
          <p className="settings-description">
            When enabled, you will receive real-time updates for any deal in your
            workspace. When disabled, you will only see notifications for deals
            where you are deal lead or deal support.
          </p>

          {notificationSettingsLoadError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {notificationSettingsLoadError}
            </Alert>
          ) : null}

          {saveNotificationSettingsError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveNotificationSettingsError}
            </Alert>
          ) : null}

          {saveSuccess ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Notification settings saved.
            </Alert>
          ) : null}

          {notificationSettingsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} sx={{ color: "#0E4664" }} />
            </Box>
          ) : (
            <>
              <div className="settings-toggle-row">
                <Switch
                  className="settings-switch"
                  checked={draftShowNotifications}
                  onChange={handleToggle}
                  disabled={!canEdit || savingNotificationSettings}
                />
                <div className="settings-toggle-copy">
                  <p className="settings-toggle-label">
                    Show all deal notifications
                  </p>
                  <p className="settings-toggle-hint">
                    {draftShowNotifications
                      ? "You're seeing notifications for every deal in the workspace."
                      : "You're only seeing notifications for deals where you are deal lead or deal support."}
                  </p>
                </div>
              </div>

              <div className="settings-save-row">
                <CustomButton
                  text="Save changes"
                  handleButtonPress={() => {
                    void handleSave();
                  }}
                  isButtonDisabled={
                    !canEdit ||
                    !isDirty ||
                    savingNotificationSettings ||
                    notificationSettingsLoading
                  }
                  isLoading={savingNotificationSettings}
                  isLoadingText="Saving"
                  widthOverride="140px"
                  solid={isDirty && !savingNotificationSettings}
                />
              </div>
            </>
          )}
        </SettingsCard>

        <SettingsCard
          id="workspace"
          title="Workspace settings"
          icon={<TuneOutlinedIcon />}
        >
          <RouterLink to="/add-templates" className="settings-link-row">
            <div className="settings-link-icon">
              <DescriptionOutlinedIcon />
            </div>
            <div className="settings-link-copy">
              <p className="settings-link-title">Templates</p>
              <p className="settings-link-description">
                Manage reusable deal and memo templates
              </p>
            </div>
            <ChevronRightIcon className="settings-link-chevron" />
          </RouterLink>

          <RouterLink to="/prompts" className="settings-link-row">
            <div className="settings-link-icon">
              <SmartToyOutlinedIcon />
            </div>
            <div className="settings-link-copy">
              <p className="settings-link-title">LLM Config</p>
              <p className="settings-link-description">
                Configure the model and prompts used across the workspace
              </p>
            </div>
            <ChevronRightIcon className="settings-link-chevron" />
          </RouterLink>
        </SettingsCard>
      </Box>
    </Box>
  );
}
